import type { Algorithm } from "./hotp";

export type Mode = keyof typeof MODES;

// The three one-time passwords, which differ in what is counted rather than in how it is hashed: TOTP counts time
// steps, HOTP counts uses, and OCRA counts a challenge and whatever else its suite names.
export const MODES = {
  totp: { label: "TOTP", title: "Time-Based OTP" },
  hotp: { label: "HOTP", title: "Counter-Based OTP" },
  ocra: { label: "OCRA", title: "Challenge-Response OTP" },
};

export const MODE_OPTIONS = Object.entries(MODES).map(([value, { label }]) => ({ value, label }));

export const ALGORITHM_OPTIONS = [
  { value: "SHA1", label: "SHA-1" },
  { value: "SHA256", label: "SHA-256" },
  { value: "SHA512", label: "SHA-512" },
];

// A key longer than the hash's block is hashed down to fit and a shorter one is padded out, so a generated secret is
// the width of the digest it will be keyed into: 160 bits is what RFC 4226 recommends, and the other two follow.
export const SECRET_SIZES: Record<Algorithm, number> = { SHA1: 20, SHA256: 32, SHA512: 64 };

export interface Range {
  min: number;
  max: number;
}

export const DIGIT_RANGE: Range = { min: 4, max: 10 };
export const PERIOD_RANGE: Range = { min: 1, max: 3600 };
// Two to the fifty-three, which is where a counter stops being a whole number in a `NumberInput`; the field it is
// written into holds twice that again, but a step nobody can type is not one this page can offer.
export const MAX_COUNTER = Number.MAX_SAFE_INTEGER;

// The last second `Date` can name, so a typed moment is one the rest of the browser would also agree exists.
export const MAX_TIME = 8_640_000_000_000;

export const COUNTER_RANGE: Range = { min: 0, max: MAX_COUNTER };
export const TIME_RANGE: Range = { min: 0, max: MAX_TIME };

export function pickMode(value: unknown): Mode {
  return typeof value === "string" && value in MODES ? value as Mode : "totp";
}

export function pickAlgorithm(value: unknown): Algorithm {
  return ALGORITHM_OPTIONS.some((option) => option.value === value) ? value as Algorithm : "SHA1";
}

export function pickText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

// For a field whose default is not blank, where a link that names it empty is a box somebody cleared and a link
// that leaves it out is one that never touched it.
export function pickTextOr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

export function parseWhole(value: number | string, { min, max }: Range): number | null {
  const parsed = typeof value === "number" ? value : parseInt(value, 10);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.floor(parsed);
  return rounded >= min && rounded <= max ? rounded : null;
}

// A shared link carries anything at all, so this lands on a usable setting rather than trust it.
export function clampWhole(value: unknown, { min, max }: Range, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(value)));
}

// What the result card says it computed, so a code carries the settings it came out of and not just the digits.
export function describeCrypto(algorithm: Algorithm, digits: number): string {
  return digits === 0 ? `HMAC-${hashLabel(algorithm)}, untruncated` : `HMAC-${hashLabel(algorithm)}, ${digits} digits`;
}

// A suite spells its hashes without the hyphen and the picker spells them with it; the page says it one way.
export function hashLabel(algorithm: Algorithm): string {
  return ALGORITHM_OPTIONS.find((option) => option.value === algorithm)?.label ?? algorithm;
}
